import { useCallback, useEffect, useRef, useState } from 'react';
import { useColor } from 'react-color-palette';
import _ from 'lodash';
import {
  useUpdatedColors,
  useUpdatedCustomizationInfo,
} from '../../../api/Brand';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import THEME from '../../../config/theme';
import { firestore } from '../../../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { queryClient } from '../../../queries/queryClient';

export const useEditMain = () => {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [linkImg1, setLinkImg1] = useState('');
  const [linkImg2, setLinkImg2] = useState('');
  const [linkImg3, setLinkImg3] = useState('');
  //use state para mostrar ou não o modal de cores
  const [show, setShow] = useState({
    showBackgroundColor: false,
    showTextApp: false,
    showTextTitleMain: false,
    showBackgroundButtonColor: false,
    showTextButtonColor: false,
    showIconLock: false,
    showIconColor: false,
  });

  const updateNavigationLinks = async () => {
    try {
      const docRef = doc(firestore, 'customization', 'images');
  
      await updateDoc(docRef, {
        'imageApp.navigation.0': linkImg1 || '',
        'imageApp.navigation.1': linkImg2 || '',
        'imageApp.navigation.2': linkImg3 || '',
      });
      // As imagens da marca ficam 1 h em cache: sem isto o banner da Main
      // continuava abrindo o link antigo.
      queryClient.invalidateQueries({ queryKey: ['IMAGE'] });
    } catch (error) {
      console.error('Erro ao salvar links de navegação:', error);
    }
  };

  useEffect(() => {
    const fetchLinks = async () => {
      const docRef = doc(firestore, 'customization', 'images');
      const snap = await getDoc(docRef);
  
      if (snap.exists()) {
        const navigation = snap.data()?.imageApp?.navigation;
  
        setLinkImg1(navigation?.['0'] || '');
        setLinkImg2(navigation?.['1'] || '');
        setLinkImg3(navigation?.['2'] || '');
      }
    };
  
    fetchLinks();
  }, []);
  
  const handleShowPicker = useCallback((field, value) => {
    setShow((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  });

  const {
    customColor: colorInfo,
    customInfo: customizationInfo,
    isLoadingCustomInfo: isLoadingCustomizationInfo,
    isLoadingColorInfo: isLoadingThemeInfo,
  } = useCustomTheme();

  const { mutateAsync: updatedColor } = useUpdatedColors();

  //instanciar as cores default
  const [backgroundColor, setBackgroundColor] = useColor(THEME.COLORS.BACKGROUND_MAIN);
  const [textTitleMain, setTextTitleMain] = useColor(THEME.COLORS.TITLE_MAIN);
  const [textApp, setTextApp] = useColor(THEME.COLORS.TEXT_MAIN);
  const [backgroundButtonColor, setBackgroundButtonColor] = useColor(THEME.COLORS.PRIMARY_900);
  const [textButtonColor, setTextButtonColor] = useColor(THEME.COLORS.TEXT_BUTTON);
  const [iconLockColor, setIconLockColor] = useColor(THEME.COLORS.ICON);
  const [iconColor, setIconColor] = useColor(THEME.COLORS.ICON_HEADER_MAIN);

  const [isSavedColorsApp, setIsSavedColorsApp] = useState(false);

  //get das cores que estao no banco, se não tiver ficará na default
  useEffect(() => {
    if (colorInfo && Object.keys(colorInfo).length > 1) {
      setBackgroundColor({
        ...backgroundColor,
        hex:
          colorInfo.background && colorInfo.background.app
            ? colorInfo.background.app
            : THEME.COLORS.BACKGROUND_MAIN,
      });
      setTextApp({
        ...textApp,
        hex:
          colorInfo.text && colorInfo.text.app
            ? colorInfo.text.app
            : THEME.COLORS.TEXT_MAIN,
      });
      setTextTitleMain({
        ...textTitleMain,
        hex:
          colorInfo.text && colorInfo.text.titleMain
            ? colorInfo.text.titleMain
            : THEME.COLORS.TITLE_MAIN,
      });
      setBackgroundButtonColor({
        ...backgroundButtonColor,
        hex:
          colorInfo.button && colorInfo.button.backgroundApp
            ? colorInfo.button.backgroundApp
            : THEME.COLORS.PRIMARY_900,
      });
      setTextButtonColor({
        ...textButtonColor,
        hex:
          colorInfo.button && colorInfo.button.textApp
            ? colorInfo.button.textApp
            : THEME.COLORS.TEXT_MAIN,
      });
      setIconLockColor({
        ...iconLockColor,
        hex:
          colorInfo.iconsMain && colorInfo.iconsMain.iconLock
            ? colorInfo.iconsMain.iconLock
            : THEME.COLORS.ICON,
      });
      setIconColor({
        ...iconColor,
        hex:
          colorInfo.iconsMain && colorInfo.iconsMain.icons
            ? colorInfo.iconsMain.icons
            : THEME.COLORS.ICON_HEADER_MAIN,
      });
    }
  }, [colorInfo]);

  const debouncedUpdateColor = useCallback(
    _.debounce((newColors) => {
      updatedColor(newColors);
    }, 500),
    []
  );
  //post para atualizar as cores
  useEffect(() => {
    const newColors = {
      background: {
        app: backgroundColor.hex,
      },
      text: {
        app: textApp.hex,
        titleMain: textTitleMain.hex,
      },
      button: {
        backgroundApp: backgroundButtonColor.hex,
        textApp: textButtonColor.hex,
      },
      iconsMain: {
        iconLock: iconLockColor.hex,
        icons: iconColor.hex,
      },
    };
    debouncedUpdateColor(newColors);
  }, [
    backgroundColor,
    textApp,
    textTitleMain,
    backgroundButtonColor,
    textButtonColor,
    iconColor,
    iconLockColor,
  ]);

  //TITULO E SUBTITULO
  const { mutateAsync: updatedCustomInfo } = useUpdatedCustomizationInfo();

  const debounceText = useCallback(
    _.debounce((text) => {
      updatedCustomInfo(text);
    }, 500),
    []
  );
  //get no titulo e subtitulo
  // Semeado uma ÚNICA vez, na primeira carga. Agora que o cache de "infos" é
  // invalidado a cada salvamento (ver api/Brand), re-semear a cada refetch
  // devolveria ao campo a versão anterior do texto no meio da digitação.
  const hasSeededTexts = useRef(false);
  useEffect(() => {
    if (!customizationInfo || hasSeededTexts.current) return;
    hasSeededTexts.current = true;
    setTitle(customizationInfo.title ?? '');
    setSubTitle(customizationInfo.subTitle ?? '');
  }, [customizationInfo]);

  //post para atualizar titulo e subtitulo
  useEffect(() => {
    const infos = { title, subTitle };
    if (Object.values(infos).some((value) => value !== undefined)) {
      debounceText(infos);
    }
  }, [title, subTitle]);

  return {
    backgroundColor,
    setBackgroundColor,
    textTitleMain,
    setTextTitleMain,
    textApp,
    setTextApp,
    backgroundButtonColor,
    setBackgroundButtonColor,
    textButtonColor,
    setTextButtonColor,
    iconLockColor,
    setIconLockColor,
    iconColor,
    setIconColor,
    show,
    handleShowPicker,
    title,
    setTitle,
    subTitle,
    setSubTitle,
    isLoadingThemeInfo,
    isLoadingCustomizationInfo,
    isSavedColorsApp,
    setIsSavedColorsApp,
    linkImg1,
    setLinkImg1,
    linkImg2,
    setLinkImg2,
    linkImg3,
    setLinkImg3,
    updateNavigationLinks,
  };
};
